import type {
  ButtonHTMLAttributes,
  ComponentProps,
  ForwardedRef,
  ReactNode,
  Ref,
} from 'react';
import type { To } from 'react-router-dom';

import { Link } from '@sorare/routing';

import { useClickHandler } from 'hooks/useClickHandler';

type NativeButtonProps = ButtonHTMLAttributes<
  HTMLButtonElement | HTMLAnchorElement
> & {
  disableDebounce?: boolean;
  ref?: Ref<HTMLButtonElement>;
};

type LinkButtonProps = {
  externalLink?: boolean;
} & Omit<ComponentProps<typeof Link>, 'to'>;

type AllButtonProps = Omit<NativeButtonProps, 'ref'> &
  Omit<LinkButtonProps, 'ref' | 'type'> & {
    ref?: Ref<HTMLButtonElement | HTMLAnchorElement>;
  };

const NativeButtonBase = (props: NativeButtonProps) => {
  const {
    onClick,
    disableDebounce = true,
    type = 'button',
    ref,
    ...rest
  } = props;
  const clickHandler = useClickHandler(onClick, disableDebounce);
  return (
    <button
      ref={ref as ForwardedRef<HTMLButtonElement>}
      onClick={clickHandler}
      /* eslint-disable react/button-has-type */
      type={type}
      {...rest}
    />
  );
};

export const ButtonBase: {
  (args: { to: To } & LinkButtonProps): ReactNode;
  (
    args: { to?: never; keepSearchParams?: never } & NativeButtonProps
  ): ReactNode;
  (args: { to?: To } & AllButtonProps): ReactNode;
} = ({ keepSearchParams, ...props }) => {
  // eslint-disable-next-line react/destructuring-assignment
  if ('to' in props && props.to != null) {
    const { externalLink, ref, ...rest } = props;
    return (
      <Link
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        keepSearchParams={keepSearchParams}
        {...rest}
        {...(externalLink
          ? ({ target: '_blank', rel: 'noopener noreferrer' } as any)
          : {})}
      />
    );
  }

  const { ref, ...rest } = props;
  return (
    <NativeButtonBase {...rest} ref={ref as ForwardedRef<HTMLButtonElement>} />
  );
};
